<template>
  <div class="page-container-wrapper">
    <loader v-if="dataLoading"/>
    <template v-else>
      <div class="page-container-header d-flex align-items-center">
        <h2 class="text-md mb-0">{{ $t('message.Guideline.publications') }}</h2>

        <b-button variant="info" class="mr-2 ml-auto" @click="goBack">{{ $t('message.return') }}</b-button>
      </div>

      <div class="container-fluid">
        <template v-if="isGuidelineSubmitter && guideline.phase === guidelinePhases.uploadGpcReviewed">
          <div class="row">
            <div class="col-12">
              <div class="card border-with-shadow">
                <div class="card-body">
                  <b-alert :show="!checkDownloaded" variant="info">{{ $t('message.Guideline.downloadReviewersFileInfo') }}</b-alert>

                  <div class="page-container-toolbar">
                    <template v-if="!checkDownloaded">
                    <span class="btn btn-primary btn text-center disabled">
                      {{ $t('message.Guideline.uploadCorrectedFiles') }}
                    </span>
                    </template>
                    <template v-else>
                      <router-link class="btn btn-primary btn text-center" :to="'/guidelines/' + guideline.id + '/upload'">
                        {{ $t('message.Guideline.uploadCorrectedFiles') }}
                      </router-link>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <div class="row">
          <!-- LEFT -->
          <div class="col-md-12 col-xl-7">
            <div class="page-container guidelines-editor">
              <div class="row">
                <div class="col-md-12">
                  <b-card v-if="showGpcUploadForm" :title="$t('message.Guideline.sectionReuploadFiles')" class="mb-2 fileUploader">
                    <p class="card-text">
                      {{ $t('message.Guideline.myGPCRole') }}
                    </p>

                    <b-form-group>
                      <b-form-radio-group v-model="changeType">
                        <b-form-radio :value="0">{{ $t('message.Guideline.uploadedReview.noChanges') }}</b-form-radio>
                        <b-form-radio :value="1">{{ $t('message.Guideline.uploadedReview.minor') }}</b-form-radio>
                        <b-form-radio :value="2">{{ $t('message.Guideline.uploadedReview.major') }}</b-form-radio>
                      </b-form-radio-group>
                    </b-form-group>

                    <template v-if="changeType > 0">
                      <b-form-textarea
                          v-model="comment"
                          class="mb-4"
                          :placeholder="$t('message.Guideline.reviewerComment')"
                          :rows="4"
                          :max-rows="6">
                      </b-form-textarea>

                      <div class="dropzone mb-4" v-show="$refs.upload && $refs.upload.drop">
                        <div :class="{ 'dragActive' : $refs.upload && $refs.upload.dropActive }">
                          <h5 v-html="$t('message.Guideline.draganddrop')"></h5>
                          <label for="gl" class="btn btn-primary">{{ $t('message.Guideline.selectFiles') }}</label>
                          <h5 style="color: red" v-html="$t('message.Guideline.draganddropUnderButtonReviewer')"></h5>
                        </div>
                      </div>

                      <template v-if="files && files.length">
                        <div class="table-responsive">
                          <b-table striped hover :items="files" :fields="['size', {key: 'name', label: $t('message.Guideline.file')}, 'type', 'progress', 'actions']">
                            <template v-slot:info="data">
                            <pre>
                              {{ data.item.response.data.id }}
                            </pre>
                            </template>

                            <template v-slot:progress="data">
                              <b-progress :value="parseInt(data.item.progress)" :max="100" show-progress animated></b-progress>
                            </template>

                            <template v-slot:actions="data">
                              <a target="_blank" :href="getFileUrl(data.item)">
                                <font-awesome-icon icon="download" size="lg" class="mr-2"/>
                              </a>

                              <a @click.prevent="deleteFileInfo(data.item)">
                                <font-awesome-icon icon="trash" size="lg" class="mr-2 text-danger"/>
                              </a>
                            </template>
                          </b-table>
                        </div>
                      </template>

                      <file-upload
                          ref="upload"
                          v-model="files"
                          :post-action="uploadUrl"
                          name="gl"
                          :headers="{'Authorization': 'Bearer ' + getAuthToken}"
                          chunk-enabled
                          :chunk="chunk"
                          :drop="true"
                          @input-filter="inputFilter"
                          @input="$refs.upload.active = true"
                          :multiple="true">
                      </file-upload>
                    </template>
                    <template v-else-if="changeType === 0">
                      <b-row class="mt-3 px-3">
                        <b-col sm="12">
                          {{ $t("message.Guideline.looksGood") }}
                        </b-col>
                      </b-row>
                    </template>

                    <b-button :disabled="!readyToSubmitted" class="float-right mr-2" variant="success" @click="submitReviewedInfo">
                      {{ $t("message.Guideline.submitReviewed") }}
                    </b-button>
                  </b-card>
                </div>
              </div>
            </div>
            <div class="page-container guidelines-editor" id="page-container">
              <b-alert v-show="dataError" class="mt-4" show variant="danger">{{ $t('message.connectionError') }}</b-alert>
              <div class="px-4">
                <div class="row">
                  <div class="col-md-12">
                    <!-- LEFT BASE DATA -->
                    <base-data-left :guideline="guideline" class="d-xl-none"></base-data-left>

                    <!-- LEFT PROGRESS -->
                    <phase-preview-left :guideline="guideline" class="d-xl-none"></phase-preview-left>

                    <!-- If phase is review end or reviewed - reviewer see own files that he upload -->
                    <b-card v-if="showReviewSection" :title="$t('message.Guideline.sectionGpcReviewed')" class="mb-2">
                      <div v-for="(review, index) in reviews" class="review-details" :key="index">

                        <b-row class="my-1 px-3">
                          <b-col sm="2"><label class="col-form-label">{{ $t("message.Guideline.reviewer") }}:</label></b-col>
                          <b-col sm="4"><label class="form-control-plaintext">{{ review.user_name }}</label></b-col>

                          <b-col sm="2"><label class="col-form-label">{{ $t("message.Guideline.changes") }}:</label></b-col>
                          <b-col sm="4"><label class="form-control-plaintext">{{ $t("message.Guideline.reviewType.change" + review.change_type) }}</label></b-col>
                        </b-row>

                        <b-row class="my-1 px-3">
                          <b-col sm="2"><label class="col-form-label">{{ $t("message.Guideline.withComment") }}:</label></b-col>
                          <b-col sm="10" class="preLine"><label class="col-form-label">
                            <template v-if="review.comment && review.comment.length > 0">{{ review.comment }}</template>
                            <template v-else>{{ $t("message.noData") }}</template>
                          </label></b-col>
                        </b-row>

                        <template v-if="review.change_type > 0 && getUserFiles(review.user_id, review.upload_id).length > 0">
                          <b-row class="my-1 px-3">
                            <b-col sm="12"><label class="col-form-label">Uploaded files:</label></b-col>
                          </b-row>

                          <b-row class="my-1 px-3">
                            <b-col sm="12">
                              <div class="table-responsive">
                                <b-table striped hover :items="getUserFiles(review.user_id, review.upload_id)"
                                         :fields="[ 'user_name', {key: 'upload_id', label: $t('message.Guideline.uploadId')}, 'size', {key: 'name', label: $t('message.Guideline.file')}, 'type', 'downloaded', 'actions']">
                                  <template v-slot:upload_id="data">
                                    {{ $t("message.Guideline.fileVersion", {version: data.item.upload_id}) }}
                                  </template>

                                  <template v-slot:actions="data">
                                    <a target="_blank" :href="getFileUrl(data.item)" @click="setDownloaded(data.item.id)">
                                      <font-awesome-icon icon="download" size="lg" class="mr-2"/>
                                    </a>
                                    <a v-if="isSuperUser" @click.prevent="deleteFileInfo(data.item)">
                                      <font-awesome-icon icon="trash" size="lg" class="mr-2 text-danger"/>
                                    </a>
                                  </template>

                                  <template v-slot:type="data">
                                    <font-awesome-icon icon="info-circle" :title="data.item.type" size="lg" class="mr-2"/>
                                  </template>

                                  <template v-slot:size="data">
                                    <font-awesome-icon icon="info-circle" :title="String(data.item.size)+'b'" size="lg" class="mr-2 text-primary"/>
                                  </template>

                                  <template v-slot:downloaded="data">
                                    {{ data.item.downloaded }}
                                  </template>
                                </b-table>
                              </div>

                              <button class="btn btn-outline-secondary" @click="prepareZippedFiles(review.user_id, review.upload_id)">
                                {{ $t('message.Guideline.downloadZipped') }}
                              </button>

                              <button v-if="isSuperUser" class="btn btn-outline-primary ml-2" @click="uploadForUser(review.user_id, review.upload_id, true)">
                                {{ $t('message.Guideline.uploadForUser') }}
                              </button>
                            </b-col>
                          </b-row>
                        </template>
                        <template v-else-if="review.change_type === 0">
                          <b-row class="mt-3 px-3">
                            <b-col sm="12">
                              {{ $t("message.Guideline.looksGood") }}
                            </b-col>
                          </b-row>
                        </template>
                      </div>
                    </b-card>

                    <b-card :title="$t('message.Guideline.sectionUploadedFiles')" class="mb-2 fileUploader">
                      <template v-if="filesUploaded && filesUploaded.length">
                        <div class="table-responsive">
                          <b-table striped hover w-auto :items="filesUploaded"
                                   :fields="['size', {key: 'upload_id', label: $t('message.Guideline.uploadId')}, {key: 'name', label: $t('message.Guideline.file')}, 'type', 'actions']">
                            <template v-slot:info="data">
                          <pre>
                            {{ data.item.response.data.id }}
                          </pre>
                            </template>

                            <template v-slot:upload_id="data">
                              {{ $t("message.Guideline.fileVersion", {version: data.item.upload_id}) }}
                            </template>

                            <template v-slot:progress="data">
                              <b-progress :value="parseInt(data.item.progress)" :max="100" show-progress animated></b-progress>
                            </template>

                            <template v-slot:type="data">
                              <font-awesome-icon icon="info-circle" :title="data.item.type" size="lg" class="mr-2"/>
                            </template>

                            <template v-slot:size="data">
                              <font-awesome-icon icon="info-circle" :title="String(data.item.size)+'b'" size="lg" class="mr-2 text-primary"/>
                            </template>

                            <template v-slot:actions="data">
                              <a target="_blank" :href="data.item.url">
                                <font-awesome-icon icon="download" size="lg" class="mr-2"/>
                              </a>

                              <a v-if="isSuperUser" @click.prevent="deleteFileInfo(data.item)">
                                <font-awesome-icon icon="trash" size="lg" class="mr-2 text-danger"/>
                              </a>

                            </template>
                          </b-table>
                        </div>
                      </template>

                      <button class="btn btn-outline-secondary" @click="prepareZippedFiles(guideline.submitter_id, '')">
                        {{ $t('message.Guideline.downloadZipped') }}
                      </button>

                      <button v-if="isSuperUser" class="btn btn-outline-primary ml-2" @click="uploadForUser(guideline.submitter_id, guideline.upload_id, false)">
                        <template v-if="isSuperUser">
                          {{ $t('message.Guideline.uploadForUser') }}
                        </template>
                        <template v-else>
                          {{ $t('message.Guideline.upload') }}
                        </template>
                      </button>
                    </b-card>
                  </div>
                </div>
              </div>
            </div>
            <admin-upload :guideline="guideline" :userId="adminUpload.userId" :uploadId="adminUpload.uploadId" :show="adminUpload.show" :isGuidelineSubmitter="isGuidelineSubmitter"
                          :isReviewUpload="adminUpload.isReviewUpload" @close="adminCloseUpload($event)"></admin-upload>
          </div>

          <!-- RIGHT -->
          <div class="d-md-none d-xl-block col-xl-5">
            <base-data-right :guideline="guideline"></base-data-right>

            <phase-preview :guideline="guideline"></phase-preview>
          </div>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import {
  GUIDELINE_REQUEST_BY_ID,
  GUIDELINE_DELETE_FILE_BY_ID,
  GUIDELINE_ADMIN_UPLOAD_APPROVE,
  GUIDELINE_CLOSE_UPLOAD_REVIEW_USER_PHASE,
  GUIDELINE_CHANGE_PHASE
} from '@/store/actions/guidelines';

import {Guideline, GuidelinePhases} from '@/lib/guideline.js';

import Api from '@/services/Api';
import FileUpload from 'vue-upload-component/dist/vue-upload-component.part.js'
import AdminUpload from '@/components/Guideline/GuidelineReview/AdminUpload'

import BaseDataRight from '@/components/Guideline/BaseData/right.vue'
import BaseDataLeft from '@/components/Guideline/BaseData/left.vue'

import PhasePreview from '@/components/Guideline/PhasePreview'
import PhasePreviewLeft from '@/components/Guideline/PhasePreview/left.vue'

export default {
  name: 'guideline-review',
  components: {
    FileUpload,
    AdminUpload,
    BaseDataRight,
    BaseDataLeft,
    PhasePreview,
    PhasePreviewLeft
  },
  props: [],
  data() {
    return {
      guideline: new Guideline,
      guidelinePhases: GuidelinePhases,

      files: [],
      filesCount: 0,
      changeType: null,
      comment: null,
      showAdminUpload: false,
      adminUpload: {
        userId: null,
        uploadId: null,
        isReviewUpload: false,
        show: false
      },
    }
  },
  computed: {
    canAddFilesToGuideline() {
      return this.isSuperUser && this.guideline.phase === this.guidelinePhases.uploadedForGpcReview;
    },

    isGuidelineSubmitter() {
      return this.getUserData.id === this.guideline.submitter_id;
    },

    checkDownloaded() {
      let reviewerFiles = this.guideline.files.filter(row => {
            return row.upload_id === this.guideline.upload_id &&
                row.user_id !== this.guideline.submitter_id &&
                row.downloaded === false
          }
      )
      return reviewerFiles.length === 0
    },

    chunk() {
      return {
        headers: {'Authorization': 'Bearer ' + this.getAuthToken},
        action: this.uploadChunkedUrl,
        maxActive: 1,
        maxRetries: 5,
      }
    },

    readyToSubmitted() {
      if (this.changeType != 2) {
        return 1;
      }

      return (this.files && this.files.length > 0) && (this.comment !== null);
    },

    filesUploaded() {
      if (this.guideline && this.guideline.files) {
        return this.guideline.files.filter(elem => {
          return elem.upload_id != null && elem.user_id === this.guideline.submitter_id;
        });
      }

      return [];
    },

    showGpcUploadForm() {
      return this.isGpc &&
          this.guideline.phase === this.guidelinePhases.uploadedForGpcReview &&
          (this.guideline.reviews &&
              this.guideline.reviews.filter(elem => {
                return elem.user_id === this.getUserData.id && elem.upload_id === this.guideline.upload_id
              }).length === 0);
    },

    showReviewSection() {
      return this.guideline.upload_id > 0 && this.guideline.reviews.length > 0;
    },

    reviews() {
      if (this.guideline && this.guideline.reviews) {
        return this.guideline.reviews.filter(elem => {
          return elem.user_type === 2
        });
      }
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    getData() {
      this.$store.dispatch(GUIDELINE_REQUEST_BY_ID, this.$route.params.id).then((response) => {
        if (response.data.data.phase < this.guidelinePhases.uploadedForGpcReview) {
          this.$router.push('/guidelines/' + response.data.data.id + '/view')
        }

        this.guideline = response.data.data;

        this.files = this.guideline.files.filter(elem => (elem.user_id === this.getUserData.id && elem.upload_id == null));

      }).catch((error) => {
        this.handleUnauth(error.response.status);
      });
    },

    goBack() {
      this.$router.push('/guidelines/')
    },

    getZipped() {
      window.open(this.guideline.zipUrl);
    },

    prepareZippedFiles(userId, uploadId) {
      /**
       * Set as downloaded for user
       */
      let files = this.guideline.files.filter(row => {
        return row.user_id === userId && row.upload_id === uploadId
      })

      for (let x in files) {
        files[x].downloaded = true
      }

      Api().get(`/guidelines/prepare-zipped/${this.guideline.id}/${userId}/${uploadId}`).then(response => {
        window.open(response.data.fileUrl);
      }).catch((error) => {
        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });
    },

    getFileUrl(file) {
      return (file.response && file.response.data !== undefined) ? file.response.data.url : (file.url !== undefined ? file.url : null);
    },

    setDownloaded(fileId) {
      let file = this.guideline.files.find(row => row.id === fileId)

      if (file) {
        this.$set(file, 'downloaded', true);
      }
    },

    deleteFileInfo(file) {
      let fileId = file.response !== undefined ? file.response.data.id : file.id;

      this.$swal({
        title: this.$t('message.areYouSure'),
        html: this.$t('message.Guideline.deleteFileInfo'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('message.yesImSure'),
        cancelButtonText: this.$t('message.noCancel'),
      }).then((result) => {
        if (result.value !== undefined) {
          this.deleteFile(fileId);
        }
      });
    },

    deleteFile(fileId) {
      this.$store.dispatch(GUIDELINE_DELETE_FILE_BY_ID, {gid: this.guideline.id, fid: fileId})
          .then(() => {
            this.getData();
          }).catch((error) => {
        this.handleUnauth(error.response.status);
      });
    },

    submitReviewedInfo() {
      this.$swal({
        title: this.$t('message.Guideline.yourFeedbackAYS'),
        html: this.$t('message.Guideline.yourFeedbackSubmitInfo'),
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$t('message.yesImSure'),
        cancelButtonText: this.$t('message.noCancel'),
      }).then((result) => {
        if (result.value !== undefined) {
          this.closeUploadReviewPhase();
        }
      });
    },

    closeUploadReviewPhase() {
      let phase = GuidelinePhases.uploadGpcReviewed

      if (this.changeType === 0) {
        phase = GuidelinePhases.uploadGpcReviewedEnd
      }

      this.$store.dispatch(GUIDELINE_CLOSE_UPLOAD_REVIEW_USER_PHASE, {id: this.guideline.id, changeType: this.changeType, comment: this.comment, userType: 2})
          .then(() => {
            this.$swal({
              title: this.$t("message.success"),
              text: this.$t("message.Guideline.yourFeedbackSubmitted"),
              type: 'success',
              confirmButtonText: 'OK'
            });
            this.getData();
          }).catch((error) => {
        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });

      this.$store.dispatch(GUIDELINE_CHANGE_PHASE, {id: this.guideline.id, phase: phase})
          .then(() => {
            this.$swal({
              title: this.$t("message.success"),
              text: this.$t("message.Guideline.yourFeedbackSubmitted"),
              type: 'success',
              confirmButtonText: 'OK'
            });
            this.getData();
          }).catch((error) => {
        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });
    },

    getUserFiles(userId, uploadId) {
      return this.guideline.files.filter(elem => {
        return elem.user_id === userId && elem.upload_id === uploadId
      });
    },

    addFilesToGuideline() {
      this.showAdminUpload = true;
    },

    addFilesToGuidelineCommit() {
      this.$store.dispatch(GUIDELINE_ADMIN_UPLOAD_APPROVE, {id: this.guideline.id})
          .then(() => {
            this.$swal({
              title: this.$t("message.success"),
              text: this.$t("message.Guideline.successfullyUploaded"),
              type: 'success',
              confirmButtonText: 'OK'
            });
            this.showAdminUpload = false;
            this.getData();
          }).catch((error) => {
        this.handleUnauth(error.response.status);
        this.handleForbidden(error.response.status);
      });
    },

    uploadForUser(userId, uploadId, isReviewUpload) {
      if (this.isSuperUser) {
        this.adminUpload.userId = userId;
        this.adminUpload.uploadId = uploadId;
        this.adminUpload.isReviewUpload = isReviewUpload;
        this.adminUpload.show = true;
      }
    },

    adminCloseUpload(event) {
      this.adminUpload.show = false
      if (event === 'reload') {
        this.getData();
      }
    }
  }
}
</script>

<style lang="scss">
.guidelines-view {
}

.dragActive {
  background: white;
}

.dropzone {
  background: #e6e6e6;
  text-align: center;
  padding: 30px;
  border: 2px dashed;

  div {
    padding: 20px;
  }
}

.col-form-label {
  text-align: left;
}

.file-uploads {
  overflow: hidden;
  position: relative;
  text-align: center;
  display: inline-block;
}

.file-uploads.file-uploads-html4 {
  input[type="file"] {
    opacity: 0;
    font-size: 20em;
    z-index: 1;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    position: absolute;
    width: 100%;
    height: 100%;
  }
}

.file-uploads.file-uploads-html5 {
  input[type="file"] {
    overflow: hidden;
    position: fixed;
    width: 1px;
    height: 1px;
    z-index: -1;
    opacity: 0;
  }
}

.review-details {
  padding: 2rem 0;
  margin-bottom: 2rem;
  //box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
  border: 1px solid #cccccc;
  border-radius: 4px;
}

.border-with-shadow {
  background-color: #ffffff;
  -webkit-box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19);
}
</style>
